import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, Inventory } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import {
  useGetBundlesQuery,
  useCreateBundleMutation,
  useDeleteBundleMutation,
} from '../store/hooks';
import type { BundleResponse } from '@store/generated/admin';

export default function BundlesListPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<BundleResponse | null>(null);
  const [newBundle, setNewBundle] = useState({
    name: '',
    description: '',
    stripe_product_id: '',
  });

  const { data, isLoading, error, refetch } = useGetBundlesQuery();
  const [createBundle, { isLoading: isCreating }] = useCreateBundleMutation();
  const [deleteBundle, { isLoading: isDeleting }] = useDeleteBundleMutation();

  const bundles = data?.bundles || [];

  const filteredBundles = useMemo(() => {
    if (!searchInput.trim()) return bundles;
    const searchLower = searchInput.toLowerCase();
    return bundles.filter(
      bundle =>
        bundle.name.toLowerCase().includes(searchLower) ||
        bundle.description?.toLowerCase().includes(searchLower) ||
        bundle.stripe_product_id.toLowerCase().includes(searchLower)
    );
  }, [bundles, searchInput]);

  const handleRowClick = useCallback(
    (bundle: BundleResponse) => {
      navigate(`/admin-v2/bundle/${bundle.bundle_id}`);
    },
    [navigate]
  );

  const handleCreateOpen = useCallback(() => {
    setNewBundle({ name: '', description: '', stripe_product_id: '' });
    setCreateDialogOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    setCreateDialogOpen(false);
    setNewBundle({ name: '', description: '', stripe_product_id: '' });
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    if (!newBundle.name.trim() || !newBundle.stripe_product_id.trim()) return;

    try {
      await createBundle({
        createBundleRequest: {
          name: newBundle.name.trim(),
          description: newBundle.description.trim() || null,
          stripe_product_id: newBundle.stripe_product_id.trim(),
        },
      }).unwrap();
      handleCreateClose();
      refetch();
    } catch (err) {
      console.error('Failed to create bundle:', err);
    }
  }, [newBundle, createBundle, handleCreateClose, refetch]);

  const handleDeleteClick = useCallback((bundle: BundleResponse, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedBundle(bundle);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedBundle(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedBundle) return;

    try {
      await deleteBundle({
        bundleId: selectedBundle.bundle_id,
      }).unwrap();
      handleDeleteClose();
      refetch();
    } catch (err) {
      console.error('Failed to delete bundle:', err);
    }
  }, [selectedBundle, deleteBundle, handleDeleteClose, refetch]);

  const columns = useMemo(
    () => [
      {
        key: 'name' as const,
        label: 'Name',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Inventory className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{value as string}</span>
          </div>
        ),
      },
      {
        key: 'description' as const,
        label: 'Description',
        render: (value: unknown) => (
          <span className="text-gray-600 max-w-xs truncate block">
            {(value as string) || '-'}
          </span>
        ),
      },
      {
        key: 'stripe_product_id' as const,
        label: 'Stripe Product ID',
        render: (value: unknown) => (
          <code className="text-xs bg-gray-100 px-2 py-1 rounded">{value as string}</code>
        ),
      },
      {
        key: 'grants' as const,
        label: 'Capabilities',
        render: (value: unknown, bundle: BundleResponse) => {
          const grants = bundle.grants || [];
          return (
            <div className="flex flex-wrap gap-1">
              {grants.length === 0 ? (
                <span className="text-gray-400 text-sm">None</span>
              ) : grants.length <= 2 ? (
                grants.map(grant => (
                  <Chip
                    key={grant.grant_id}
                    label={grant.capability_name || grant.capability_id}
                    size="small"
                    variant="outlined"
                  />
                ))
              ) : (
                <Chip label={`${grants.length} capabilities`} size="small" color="primary" />
              )}
            </div>
          );
        },
      },
      {
        key: 'created_at' as const,
        label: 'Created',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const date = new Date(value as string);
          return (
            <div className="text-sm">
              <div className="text-gray-900">{formatDateTime(date)}</div>
              <div className="text-gray-500">{formatRelativeTime(date)}</div>
            </div>
          );
        },
      },
      {
        key: 'bundle_id' as const,
        label: 'Actions',
        render: (_: unknown, bundle: BundleResponse) => (
          <Tooltip title="Delete bundle">
            <IconButton
              size="small"
              onClick={e => handleDeleteClick(bundle, e)}
              color="error"
              aria-label="Delete bundle"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleDeleteClick]
  );

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading bundles</h2>
            <p className="text-gray-600 mt-2">Unable to fetch bundles list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Bundles</h1>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateOpen}>
              Create Bundle
            </Button>
          </div>

          <Card className="mb-6">
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search bundles by name, description, or Stripe product ID..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                size="small"
              />
            </CardContent>
          </Card>

          {isLoading && !data ? (
            <LoadingState message="Loading bundles..." />
          ) : (
            <DataTable
              title="Bundles"
              columns={columns}
              data={filteredBundles}
              total={filteredBundles.length}
              loading={isLoading}
              onRowClick={handleRowClick}
              emptyMessage="No bundles found"
            />
          )}
        </div>
      </main>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create Bundle</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newBundle.name}
            onChange={e => setNewBundle(prev => ({ ...prev, name: e.target.value }))}
            required
            helperText="Display name for this bundle (e.g., 'Pro Plan', 'Enterprise')"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newBundle.description}
            onChange={e => setNewBundle(prev => ({ ...prev, description: e.target.value }))}
            helperText="Optional description of what this bundle includes"
          />
          <TextField
            margin="dense"
            label="Stripe Product ID"
            fullWidth
            value={newBundle.stripe_product_id}
            onChange={e => setNewBundle(prev => ({ ...prev, stripe_product_id: e.target.value }))}
            required
            helperText="The Stripe product ID linked to this bundle (e.g., 'prod_xxx')"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose}>Cancel</Button>
          <Button
            onClick={handleCreateSubmit}
            variant="contained"
            disabled={
              !newBundle.name.trim() || !newBundle.stripe_product_id.trim() || isCreating
            }
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Bundle</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the bundle "{selectedBundle?.name}"? This action cannot
            be undone and will remove all capability grants associated with this bundle.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
